/**
 * @file frameDragEvent.ts
 * @description Event system for dragging frames from ProgramMonitor and canvas nodes.
 * Supports two modes:
 * 1. DraggedFrame (with blob) - for HTML5 drag-and-drop from ProgramMonitor
 * 2. FrameReference (assetId + timestamp) - for mouse-based drags from canvas nodes
 *
 * FrameReference is preferred as it's more efficient and future-proof:
 * - No eager blob creation during drag
 * - Consumer decides how to get the frame (from cache, from backend, etc.)
 * - Works with proxy videos now, can upgrade to full-res from backend later
 */

/**
 * Data for a frame being dragged from the program monitor (HTML5 drag-and-drop)
 * Contains the actual blob data for immediate use
 */
export interface DraggedFrame {
  blob: Blob
  width: number
  height: number
  timestamp: number
  sourceAssetId: string
}

/**
 * Reference to a frame position (for mouse-based drags)
 * Consumer grabs the actual frame data when needed
 */
export interface FrameReference {
  assetId: string
  timestamp: number // in seconds
}

/**
 * Custom MIME type to identify program monitor frame drags
 */
export const FRAME_DRAG_MIME_TYPE = 'application/x-martini-frame'

/**
 * Temporary storage for the dragged frame (HTML5 drag-and-drop)
 * Only one frame can be dragged at a time
 */
let draggedFrame: DraggedFrame | null = null

/**
 * Temporary storage for frame reference (mouse-based drag)
 */
let frameReference: FrameReference | null = null

/**
 * Retrieve and clear the dragged frame
 */
export function consumeDraggedFrame(): DraggedFrame | null {
  const frame = draggedFrame
  draggedFrame = null
  return frame
}

/**
 * Clear the dragged frame without consuming it
 * (e.g., on drag end without drop)
 */
export function clearDraggedFrame(): void {
  draggedFrame = null
}

// --- Frame Reference API (for mouse-based drags) ---

/**
 * Store a frame reference for mouse-based dragging
 */
export function setFrameReference(ref: FrameReference): void {
  frameReference = ref
}

/**
 * Retrieve and clear the frame reference
 */
export function consumeFrameReference(): FrameReference | null {
  const ref = frameReference
  frameReference = null
  return ref
}

/**
 * Check if there's a frame reference available
 */
export function hasFrameReference(): boolean {
  return frameReference !== null
}

/**
 * Peek at the frame reference without consuming it.
 * Use to decide drop handling (e.g. image asset → URL path, video → decode path).
 */
export function getFrameReference(): FrameReference | null {
  return frameReference
}

/**
 * Clear the frame reference without consuming it
 */
export function clearFrameReference(): void {
  frameReference = null
}
